import YAML from 'yaml';
import type { ParsedNode, ParsedEdge, DeclaredInput } from '../types';

/**
 * Sort nodes in topological order (BFS from nodes with no incoming edges).
 * Ties are broken by node id for deterministic output.
 */
function topologicalSort( nodes: ParsedNode[], edges: ParsedEdge[]): ParsedNode[] {
  const nodeMap = new Map<string, ParsedNode>();
  const inDegree = new Map<string, number>();
  const adjacency = new Map<string, string[]>();

  for ( const node of nodes ) {
    nodeMap.set( node.id, node );
    inDegree.set( node.id, 0 );
    adjacency.set( node.id, []);
  }

  for ( const edge of edges ) {
    inDegree.set( edge.target, ( inDegree.get( edge.target ) || 0 ) + 1 );
    const adj = adjacency.get( edge.source );
    if ( adj ) {
      adj.push( edge.target );
    }
  }

  // Start with nodes that have no incoming edges, sorted by id
  const queue: string[] = [];
  for ( const node of nodes ) {
    if (( inDegree.get( node.id ) || 0 ) === 0 ) {
      queue.push( node.id );
    }
  }
  queue.sort();

  const result: ParsedNode[] = [];
  const visited = new Set<string>();

  while ( queue.length > 0 ) {
    const nodeId = queue.shift()!;
    if ( visited.has( nodeId )) continue;
    visited.add( nodeId );

    const node = nodeMap.get( nodeId );
    if ( node ) {
      result.push( node );
    }

    const neighbors = ( adjacency.get( nodeId ) || []).slice().sort();
    for ( const neighbor of neighbors ) {
      const newDegree = ( inDegree.get( neighbor ) || 1 ) - 1;
      inDegree.set( neighbor, newDegree );
      if ( newDegree === 0 && !visited.has( neighbor )) {
        // Insert into queue maintaining sorted order
        const insertIdx = queue.findIndex(( id ) => id > neighbor );
        if ( insertIdx === -1 ) {
          queue.push( neighbor );
        } else {
          queue.splice( insertIdx, 0, neighbor );
        }
      }
    }
  }

  // Append any remaining nodes not reachable (e.g., cycles or disconnected)
  for ( const node of nodes ) {
    if ( !visited.has( node.id )) {
      result.push( node );
    }
  }

  return result;
}

/**
 * Check if a values object is empty — i.e. it has no key carrying a value.
 *
 * `null` counts as a VALUE, not as absence. It used to be treated as absence, which made the
 * generator inconsistent with itself: `{ a: 1, b: null }` was emitted WITH `b: null`, while
 * `{ b: null }` collapsed to no `values` at all. That asymmetry is data loss on a graph
 * nobody edited — `nodeSignature` compares `values ?? {}` deeply, so `{ operator: null }`
 * and `{}` are different nodes (TV1-2377 slice 9 retry, item 3).
 */
function isEmptyValues( values: Record<string, unknown> | undefined ): boolean {
  if ( !values ) return true;
  const keys = Object.keys( values );
  if ( keys.length === 0 ) return true;
  return keys.every(( k ) => values[k] === undefined );
}

/**
 * Check if an inputsConfig object is empty.
 */
function isEmptyInputsConfig( inputsConfig: Record<string, unknown> | undefined ): boolean {
  if ( !inputsConfig ) return true;
  return Object.keys( inputsConfig ).length === 0;
}

/**
 * Determine if a handle name is the default first handle for a node.
 * The default is the first handle in the node's handle definitions.
 */
function isDefaultHandle( handleName: string | undefined, handles: Array<{ name: string }> ): boolean {
  if ( !handleName ) return true;
  // When handles list is empty (dynamic handles like Input/Properties), always preserve the name
  if ( handles.length === 0 ) return false;
  return handles[0].name === handleName;
}

/**
 * Build the edges array for a single node from its ParsedEdge list.
 * Edges are sorted by target node id for stable output.
 */
function buildEdges(
  nodeEdges: ParsedEdge[],
  sourceNode: ParsedNode,
  nodeMap: Map<string, ParsedNode>
): Array<Record<string, unknown>> {
  const sorted = [...nodeEdges].sort(( a, b ) => a.target.localeCompare( b.target ));

  return sorted.map(( edge ) => {
    const edgeObj: Record<string, unknown> = {
      to: edge.target
    };

    // Include fromOutput unless it's the only output or the default first output
    if ( edge.sourceHandle && !isDefaultHandle( edge.sourceHandle, sourceNode.outputs )) {
      edgeObj.fromOutput = edge.sourceHandle;
    }

    // Include toInput unless it's the only input or the default first input
    const targetNode = nodeMap.get( edge.target );
    if ( edge.targetHandle && targetNode && !isDefaultHandle( edge.targetHandle, targetNode.inputs )) {
      edgeObj.toInput = edge.targetHandle;
    }

    // Only include optional when true
    if ( edge.optional ) {
      edgeObj.optional = true;
    }

    return edgeObj;
  });
}

/**
 * Convert ParsedNode[], ParsedEdge[], and DeclaredInput[] to a complete YAML string.
 *
 * Produces YAML compatible with the DslSchema format used by the rules engine.
 * Nodes are ordered topologically (no incoming edges first, then BFS).
 * Edges within each node are sorted by target node id.
 */
export function generateDsl(
  nodes: ParsedNode[],
  edges: ParsedEdge[],
  declaredInputs: DeclaredInput[]
): string {
  // Build lookup maps
  const nodeMap = new Map<string, ParsedNode>();
  for ( const node of nodes ) {
    nodeMap.set( node.id, node );
  }

  // Group edges by source node
  const edgesBySource = new Map<string, ParsedEdge[]>();
  for ( const edge of edges ) {
    if ( !edgesBySource.has( edge.source )) {
      edgesBySource.set( edge.source, []);
    }
    edgesBySource.get( edge.source )!.push( edge );
  }

  // Sort nodes topologically
  const sortedNodes = topologicalSort( nodes, edges );

  // Convert nodes to DSL format
  const dslNodes = sortedNodes.map(( node ) => {
    const dslNode: Record<string, unknown> = {
      type: node.type
    };

    // Only include id when it differs from type
    if ( node.id !== node.type ) {
      dslNode.id = node.id;
    }

    // A wrapper's nested `source`, re-emitted exactly as it was parsed. It is not a graph
    // member and the editor never edits it, so anything other than a verbatim copy here is
    // data loss on a graph the reviewer did not touch.
    if ( node.source ) {
      dslNode.source = node.source;
    }

    // Include inputsConfig if present and non-empty
    if ( !isEmptyInputsConfig( node.inputsConfig )) {
      dslNode.inputsConfig = node.inputsConfig;
    }

    // Include values if present and non-empty
    if ( !isEmptyValues( node.values )) {
      dslNode.values = node.values;
    }

    // Include edges if present
    const nodeEdges = edgesBySource.get( node.id ) || [];
    if ( nodeEdges.length > 0 ) {
      dslNode.edges = buildEdges( nodeEdges, node, nodeMap );
    }

    return dslNode;
  });

  // Build the top-level DSL object
  const dsl: Record<string, unknown> = {
    nodes: dslNodes
  };

  // Include declaredInputs if present and non-empty
  if ( declaredInputs.length > 0 ) {
    dsl.declaredInputs = declaredInputs.map(( di ) => ({
      nodeId: di.nodeId,
      inputName: di.inputName
    }));
  }

  // Convert to YAML with 2-space indentation and no line wrapping
  return YAML.stringify( dsl, {
    indent: 2,
    lineWidth: 0
  });
}
